import React, { useMemo, useState, useEffect } from 'react';
import { Check } from 'lucide-react';

/**
 * Stage 2 grip picker.
 *
 * One card per (brand, model). Each card surfaces:
 *   • a row of color swatches (one per color SKU family)
 *   • a row of size chips for the selected color
 *
 * Both `model` and `color` are parsed out of the option's `name` string —
 * no backend migration required. Stage 1 size-stripping is preserved and
 * extended with compound sizes ("Standard Jumbo", "Standard Oversize") so
 * names like "Tour Series Blackout - Standard Jumbo" collapse into the
 * Blackout color + Standard-Jumbo size cell instead of getting their own
 * card.
 */

const SIZE_DEFS = [
  // Listed longest-first so compound sizes match before their parts.
  // Optional leading hyphen handles names like "Blackout - X-Small".
  { regex: /\s*-?\s*(Standard\s+Oversize)$/i, norm: 'Standard Oversize', rank: 7 },
  { regex: /\s*-?\s*(Standard\s+Jumbo)$/i,    norm: 'Standard Jumbo',    rank: 6 },
  { regex: /\s*-?\s*(Extra\s+Small)$/i,       norm: 'Extra Small',       rank: 0 },
  { regex: /\s*-?\s*(X[\s-]Small)$/i,         norm: 'X-Small',           rank: 0 },
  { regex: /\s*-?\s*(Extra\s+Large)$/i,       norm: 'Extra Large',       rank: 5 },
  { regex: /\s*-?\s*(X[\s-]Large)$/i,         norm: 'X-Large',           rank: 5 },
  { regex: /\s*-?\s*(Undersize)$/i,           norm: 'Undersize',         rank: 1 },
  { regex: /\s*-?\s*(Small)$/i,               norm: 'Small',             rank: 1 },
  { regex: /\s*-?\s*(Standard)$/i,            norm: 'Standard',          rank: 2 },
  { regex: /\s*-?\s*(Medium)$/i,              norm: 'Medium',            rank: 2 },
  { regex: /\s*-?\s*(Midsize)$/i,             norm: 'Midsize',           rank: 3 },
  { regex: /\s*-?\s*(Large)$/i,               norm: 'Large',             rank: 3 },
  { regex: /\s*-?\s*(Jumbo)$/i,               norm: 'Jumbo',             rank: 4 },
  { regex: /\s*-?\s*(Oversize)$/i,            norm: 'Oversize',          rank: 5 },
  { regex: /\s*-?\s*(Junior)$/i,              norm: 'Junior',            rank: 8 },
];
const SIZE_RANK = SIZE_DEFS.reduce((acc, def) => {
  acc[def.norm] = def.rank;
  return acc;
}, {});
const ALIGN_FLAG = 'Align';

// Color vocabulary used to peel color tokens off the end of a model+color
// stem. Compound colors (e.g. "Jet Black", "Dark Gray", "Sky Blue") are
// handled by walking the last 1–3 tokens of the stem.
const COLOR_WORDS = new Set([
  'black', 'white', 'red', 'blue', 'green', 'yellow', 'orange', 'purple',
  'pink', 'gray', 'grey', 'brown', 'silver', 'gold', 'copper', 'navy',
  'lime', 'lavender', 'camo', 'blackout', 'jet', 'dark', 'sky',
  'tan', 'olive', 'teal', 'cyan', 'magenta',
]);

function isColorToken(tok) {
  if (!tok) return false;
  if (tok.includes('/')) return true;
  return COLOR_WORDS.has(tok.toLowerCase());
}

function stripBrand(name, brand) {
  if (!brand) return name;
  if (name.toLowerCase().startsWith(brand.toLowerCase())) {
    return name.slice(brand.length).trim();
  }
  return name;
}

function parseGripName(name, brand) {
  let stem = stripBrand(name || '', brand || '').trim();
  let size = null;
  for (const def of SIZE_DEFS) {
    const m = stem.match(def.regex);
    if (m) {
      size = def.norm;
      stem = stem.slice(0, m.index).replace(/[\s-]+$/, '').trim();
      break;
    }
  }
  let align = false;
  let tokens = stem.split(/\s+/).filter(Boolean);
  if (tokens.length && tokens[tokens.length - 1] === ALIGN_FLAG) {
    align = true;
    tokens.pop();
  }
  while (tokens.length && tokens[tokens.length - 1] === '-') tokens.pop();
  stem = tokens.join(' ').replace(/[\s-]+$/, '').trim();
  if (!stem) stem = stripBrand(name || '', brand || '').trim();

  const stemTokens = stem.split(/\s+/).filter(Boolean);
  const colorTokens = [];
  while (stemTokens.length > 0 && isColorToken(stemTokens[stemTokens.length - 1])) {
    colorTokens.unshift(stemTokens.pop());
  }
  let model = stemTokens.join(' ').trim();
  let color = colorTokens.join(' ').trim();
  if (!model) {
    model = color || stem;
    color = '';
  }
  return { model, color, size, align };
}

function groupOptions(options) {
  const byModel = new Map();
  for (const opt of options || []) {
    const { model, color, size, align } = parseGripName(opt.name || '', opt.brand || '');
    const groupKey = `${opt.brand || ''}|${model}`;
    if (!byModel.has(groupKey)) {
      byModel.set(groupKey, {
        key: groupKey,
        brand: opt.brand,
        model,
        colors: new Map(),
      });
    }
    const g = byModel.get(groupKey);
    const colorKey = `${color || ''}|${align ? 'A' : ''}`;
    if (!g.colors.has(colorKey)) {
      g.colors.set(colorKey, {
        key: colorKey,
        name: color || '',
        align,
        image_url: opt.image_url,
        price_modifier: opt.price_modifier,
        _override_applied: opt._override_applied,
        variants: [],
      });
    }
    g.colors.get(colorKey).variants.push({ ...opt, _size: size, _color: color, _align: align });
  }
  const groups = [];
  for (const g of byModel.values()) {
    const colors = [];
    for (const c of g.colors.values()) {
      c.variants.sort((a, b) => {
        const ai = SIZE_RANK[a._size] ?? 99;
        const bi = SIZE_RANK[b._size] ?? 99;
        if (ai !== bi) return ai - bi;
        return (a.name || '').localeCompare(b.name || '');
      });
      c.hasSizes = c.variants.some((v) => v._size);
      colors.push(c);
    }
    colors.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    groups.push({ ...g, colors });
  }
  return groups;
}

// Picks a CSS background out of the color name. Falls back to a neutral
// gray dot for unknown / empty colors so the UI never shows a broken circle.
const COLOR_MAP = {
  black: '#0f172a',
  blackout: '#0f172a',
  jetblack: '#0a0a0a',
  white: '#f8fafc',
  red: '#dc2626',
  blue: '#2563eb',
  skyblue: '#38bdf8',
  green: '#16a34a',
  lime: '#84cc16',
  yellow: '#eab308',
  orange: '#f97316',
  purple: '#9333ea',
  pink: '#ec4899',
  gray: '#9ca3af',
  grey: '#9ca3af',
  darkgray: '#4b5563',
  darkgrey: '#4b5563',
  brown: '#92400e',
  silver: '#cbd5e1',
  gold: '#d4a55a',
  copper: '#b45309',
  navy: '#1e3a8a',
  lavender: '#c4b5fd',
  camo: '#65a30d',
  tan: '#d6c19c',
  olive: '#65643d',
  teal: '#14b8a6',
};
const COLOR_KEYS_BY_LEN = Object.keys(COLOR_MAP).sort((a, b) => b.length - a.length);

// Words that follow another color word but together still mean a SINGLE
// color (so "Dark Gray" is one tone, not a Dark/Gray gradient). Everything
// outside this list is treated as a multi-tone color → rendered as a split
// swatch gradient.
const SINGLE_COMPOUND_PREFIXES = new Set(['dark', 'jet', 'sky', 'light']);

function colorStyle(name) {
  if (!name) return { background: '#9CA3AF' };
  // Slash-joined compound (e.g. "Black/Lime", "Grey/Blue") → gradient
  if (name.includes('/')) {
    const parts = name.split('/').map((p) => p.trim().toLowerCase().replace(/\s+/g, ''));
    const c1 = COLOR_MAP[parts[0]] || '#9ca3af';
    const c2 = COLOR_MAP[parts[1]] || '#9ca3af';
    return { background: `linear-gradient(135deg, ${c1} 50%, ${c2} 50%)` };
  }
  // Space-joined compound: "Black White" / "Black Red" → 2-tone gradient,
  // UNLESS the first token is a known modifier ("Dark Gray", "Jet Black",
  // "Sky Blue"), in which case the whole thing is a single color.
  const tokens = name.trim().toLowerCase().split(/\s+/);
  if (tokens.length >= 2 && !SINGLE_COMPOUND_PREFIXES.has(tokens[0])) {
    const colorToks = tokens.filter((t) => COLOR_MAP[t]);
    if (colorToks.length >= 2) {
      const c1 = COLOR_MAP[colorToks[0]];
      const c2 = COLOR_MAP[colorToks[1]];
      return { background: `linear-gradient(135deg, ${c1} 50%, ${c2} 50%)` };
    }
  }
  // Single-color (or modifier-prefixed): find the longest matching key
  const norm = name.toLowerCase().replace(/\s+/g, '');
  for (const k of COLOR_KEYS_BY_LEN) {
    if (norm.includes(k)) return { background: COLOR_MAP[k] };
  }
  return { background: '#9CA3AF' };
}

function GripGroupCard({ group, isSelectedGroup, selectedColorKey, selectedSize, onChange }) {
  const defaultColorKey = selectedColorKey || group.colors[0]?.key;
  const [activeColorKey, setActiveColorKey] = useState(defaultColorKey);
  // Per-color image-load failure map. We swap the broken <img> for the
  // neutral "No image" placeholder so we never show the browser's broken-
  // image icon (some CDNs / older browsers occasionally fail to load grip
  // images even when the asset returns 200 elsewhere).
  const [brokenColors, setBrokenColors] = useState({});

  useEffect(() => {
    if (selectedColorKey) setActiveColorKey(selectedColorKey);
  }, [selectedColorKey]);

  const activeColor =
    group.colors.find((c) => c.key === activeColorKey) || group.colors[0];
  const showSwatches = group.colors.length > 1;
  const showSizes = activeColor?.hasSizes;
  const imageBroken = brokenColors[activeColor?.key];
  const hasImage = !!activeColor?.image_url && !imageBroken;

  const handlePickDefault = () => {
    onChange(activeColor?.variants?.[0]?.id);
  };

  const handleSwatchClick = (c) => {
    setActiveColorKey(c.key);
    if (isSelectedGroup) {
      const sameSize =
        selectedSize && c.variants.find((v) => v._size === selectedSize);
      onChange((sameSize || c.variants[0])?.id);
    }
  };

  return (
    <div
      className={`relative p-3 rounded-md border text-left transition-all ${
        isSelectedGroup
          ? 'border-[#334155] bg-white ring-2 ring-[#334155]/20'
          : 'border-gray-200 bg-white hover:border-gray-400'
      }`}
      data-testid={`grip-group-${group.key}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={handlePickDefault}
          className="flex-shrink-0 w-24 h-14 bg-gray-100 rounded overflow-hidden flex items-center justify-center"
          data-testid={`grip-card-image-${group.key}`}
        >
          {hasImage ? (
            <img
              src={activeColor.image_url}
              alt={`${group.model} ${activeColor.name}`.trim()}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={() =>
                setBrokenColors((prev) => ({ ...prev, [activeColor.key]: true }))
              }
            />
          ) : (
            <div
              className="w-full h-full flex flex-col items-center justify-center text-center"
              style={colorStyle(activeColor?.name)}
            >
              <span className="text-[9px] font-semibold uppercase tracking-wider text-white/90 drop-shadow">
                {activeColor?.name || group.model}
              </span>
            </div>
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm font-semibold text-gray-900 leading-tight">
              {group.model}
              {activeColor?.align ? (
                <span className="ml-1 text-[10px] uppercase text-[#334155] font-semibold align-middle">
                  Align
                </span>
              ) : null}
            </p>
            <p className="text-[11px] whitespace-nowrap">
              {activeColor?._override_applied && activeColor?.price_modifier === 0 ? (
                <span className="font-semibold text-green-700">Included</span>
              ) : activeColor?.price_modifier > 0 ? (
                <span className="text-gray-500">+${activeColor.price_modifier}</span>
              ) : (
                <span className="text-gray-500">No upcharge</span>
              )}
            </p>
          </div>
          {activeColor?.name ? (
            <p className="text-[11px] text-gray-500 mt-0.5">
              Color: <span className="font-medium text-gray-700">{activeColor.name}</span>
            </p>
          ) : null}
        </div>

        {isSelectedGroup ? (
          <div className="absolute top-2 right-2 bg-[#334155] text-white rounded-full p-0.5">
            <Check className="w-3 h-3" />
          </div>
        ) : null}
      </div>

      {showSwatches ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {group.colors.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSwatchClick(c);
              }}
              title={`${c.name || 'Default'}${c.align ? ' (Align)' : ''}`}
              className={`relative w-6 h-6 rounded-full border-2 transition-all ${
                c.key === activeColorKey
                  ? 'border-[#334155] scale-110 shadow-sm'
                  : 'border-gray-300 hover:border-gray-500'
              }`}
              style={colorStyle(c.name)}
              data-testid={`grip-color-${group.key}-${c.key}`}
            >
              {c.align ? (
                <span className="absolute -bottom-1 -right-1 text-[8px] bg-[#D4A55A] text-white rounded-full px-1 leading-tight">
                  A
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {showSizes ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {activeColor.variants.map((v) => {
            const isActiveSize =
              isSelectedGroup &&
              activeColorKey === selectedColorKey &&
              v._size === selectedSize;
            return (
              <button
                key={v.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(v.id);
                }}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  isActiveSize
                    ? 'bg-[#334155] text-white border-[#334155]'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-[#334155]'
                }`}
                data-testid={`grip-size-${v.id}`}
              >
                {v._size || 'One size'}
              </button>
            );
          })}
        </div>
      ) : (
        <button
          type="button"
          onClick={handlePickDefault}
          className="mt-2 text-[11px] text-[#334155] font-semibold hover:underline"
          data-testid={`grip-pick-${group.key}`}
        >
          Select this grip
        </button>
      )}
    </div>
  );
}

export default function GripPicker({ options, value, onChange, required, testId }) {
  const brands = useMemo(() => {
    const order = [
      'Golf Pride', 'Winn', 'Iomic', 'JumboMax', 'Superstroke',
      'Grip Master', 'NO1', 'Unbranded', 'No Grip', 'Other',
    ];
    const seen = new Set();
    for (const o of options || []) if (o.brand) seen.add(o.brand);
    const list = order.filter((b) => seen.has(b));
    for (const b of seen) if (!list.includes(b)) list.push(b);
    return list;
  }, [options]);

  const [brand, setBrand] = useState(brands[0] || null);

  const filtered = useMemo(() => {
    if (!options) return [];
    if (!brand) return options;
    return options.filter((o) => (o.brand || 'Other') === brand);
  }, [options, brand]);

  const groups = useMemo(() => groupOptions(filtered), [filtered]);

  const selectedOpt = options?.find((o) => o.id === value);
  const selectedParsed = selectedOpt
    ? parseGripName(selectedOpt.name || '', selectedOpt.brand || '')
    : null;
  const selectedModelKey = selectedOpt
    ? `${selectedOpt.brand || ''}|${selectedParsed.model}`
    : null;
  const selectedColorKey = selectedParsed
    ? `${selectedParsed.color || ''}|${selectedParsed.align ? 'A' : ''}`
    : null;

  return (
    <div data-testid={testId || 'grip-picker'}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-[#334155] uppercase tracking-widest">
          Grip {required ? <span className="text-[#D4A55A] ml-1">*</span> : null}
        </span>
        {selectedOpt ? (
          <span className="text-xs text-gray-600">
            Selected: <span className="font-semibold text-gray-900">{selectedOpt.name}</span>
            {selectedOpt._override_applied && selectedOpt.price_modifier === 0 ? (
              <span className="ml-1 text-green-700 font-semibold">(Included)</span>
            ) : selectedOpt.price_modifier > 0 ? (
              <span className="ml-1 text-[#334155]">(+${selectedOpt.price_modifier})</span>
            ) : null}
          </span>
        ) : null}
      </div>

      {brands.length > 1 ? (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {brands.map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBrand(b)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                brand === b
                  ? 'bg-[#334155] text-white border-[#334155]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`grip-brand-${b}`}
            >
              {b}
            </button>
          ))}
        </div>
      ) : null}

      <div
        className={`grid grid-cols-1 gap-2 max-h-[480px] overflow-y-auto pr-1 border rounded-lg p-2 bg-gray-50 ${
          required && !value ? 'border-[#D4A55A]' : 'border-[#F3F4F6]'
        }`}
      >
        {groups.map((g) => (
          <GripGroupCard
            key={g.key}
            group={g}
            isSelectedGroup={g.key === selectedModelKey}
            selectedColorKey={g.key === selectedModelKey ? selectedColorKey : null}
            selectedSize={g.key === selectedModelKey ? selectedParsed?.size : null}
            onChange={onChange}
          />
        ))}
        {groups.length === 0 ? (
          <p className="col-span-full text-center text-sm text-gray-500 py-6">
            No grips in this category.
          </p>
        ) : null}
      </div>
    </div>
  );
}
